import { z } from 'zod';

import { TAXATION_MODES } from '../constants.js';

import { isoDateTimeSchema, uuidSchema } from './common.js';

const moneySchema = z.coerce.number().min(0).max(99_999_999.99);
const rateSchema = z.coerce.number().min(0).max(1); // 0..1 share

// ---------- Employee costs ----------

export const employeeCostSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  monthlySalary: moneySchema,
  billingRate: moneySchema,
  effectiveFromMonth: z.string().regex(/^\d{4}-\d{2}$/),
  endedFromMonth: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type EmployeeCostDto = z.infer<typeof employeeCostSchema>;

export const upsertEmployeeCostSchema = z.object({
  userId: uuidSchema,
  monthlySalary: moneySchema,
  billingRate: moneySchema,
  /** forward = new row from current month; correct = edit the active row in place. */
  mode: z.enum(['forward', 'correct']).optional(),
});
export type UpsertEmployeeCostDto = z.infer<typeof upsertEmployeeCostSchema>;

// ---------- Expenses ----------

export const managementExpenseSchema = z.object({
  id: uuidSchema,
  description: z.string().min(1).max(255),
  amount: moneySchema,
  effectiveFromMonth: z.string().regex(/^\d{4}-\d{2}$/),
  endedFromMonth: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type ManagementExpenseDto = z.infer<typeof managementExpenseSchema>;

export const createExpenseSchema = z.object({
  description: z.string().min(1, 'Введите описание').max(255),
  amount: moneySchema,
});
export type CreateExpenseDto = z.infer<typeof createExpenseSchema>;

export const updateExpenseSchema = createExpenseSchema
  .extend({
    mode: z.enum(['forward', 'correct']).default('forward'),
  })
  .partial();
export type UpdateExpenseDto = z.infer<typeof updateExpenseSchema>;

// ---------- Workspace settings (taxation) ----------

export const workspaceSettingsSchema = z.object({
  taxationMode: z.enum(TAXATION_MODES),
  salaryTaxRate: rateSchema,
  /** Hard assignee cap per allocation month; null = unlimited assignment. */
  assigneeMonthlyHoursCap: z.number().int().min(1).max(999).nullable(),
  updatedAt: isoDateTimeSchema,
});
export type WorkspaceSettingsDto = z.infer<typeof workspaceSettingsSchema>;

export const updateWorkspaceSettingsSchema = z
  .object({
    taxationMode: z.enum(TAXATION_MODES),
    salaryTaxRate: rateSchema,
    assigneeMonthlyHoursCap: z.number().int().min(1).max(999).nullable(),
  })
  .partial();
export type UpdateWorkspaceSettingsDto = z.infer<typeof updateWorkspaceSettingsSchema>;

// ---------- Break-even response ----------

export const breakEvenResponseSchema = z.object({
  totalSalaries: z.number(),
  totalSalariesWithTax: z.number(),
  totalExpenses: z.number(),
  totalCost: z.number(),
  taxationMode: z.enum(TAXATION_MODES),
  salaryTaxRate: rateSchema,
  /** Effective revenue tax rate applied to break-even formula. */
  revenueTaxRate: z.number(),
  /** Revenue required this month to cover costs after tax. */
  requiredRevenue: z.number(),
  /** Lowest positive client rate among employees — conservative basis for required hours (legacy parity). */
  minBillingRate: z.number(),
  /** Required total billable hours this month. */
  requiredHours: z.number(),
  /** 0..1 — share of the month elapsed. */
  monthProgress: z.number(),
  /** Days remaining (including today). */
  daysRemaining: z.number(),
});
export type BreakEvenResponseDto = z.infer<typeof breakEvenResponseSchema>;

// ---------- Analytics response ----------

/** Per-employee workload row: assigned vs done task-hours in active projects. */
export const employeeWorkloadSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  title: z.string(),
  /** Σ task.timeHours assigned to this user across active projects (monthly budget model). */
  assignedHours: z.number(),
  /** Σ task.timeHours of that user's DONE tasks. */
  doneHours: z.number(),
});
export type EmployeeWorkloadDto = z.infer<typeof employeeWorkloadSchema>;

/**
 * Management analytics for a given month — drives the pace health bar,
 * employee-workload bars and the plan/fact/done summary. Mirrors the legacy
 * `renderManagementPage` analytics block.
 */
export const managementAnalyticsResponseSchema = z.object({
  /** Selected month, YYYY-MM. */
  monthKey: z.string(),
  /** Months offered in the selector (current + months with tasks, ≤ current), newest first. */
  availableMonths: z.array(z.string()),
  /** 0..1 — elapsed share of the SELECTED month (1 when the month is fully in the past). */
  monthProgress: z.number(),
  /** Conservative break-even hours (= requiredRevenue / minBillingRate), 0 if no positive rate. */
  breakEvenHours: z.number(),
  /** Lowest positive employee billing rate (legacy parity). */
  minBillingRate: z.number(),
  /** Σ billable revenue for the selected month (monthKey). */
  completedBillableRevenueSelectedMonth: z.number(),
  /** Commercial done task-hours billed in the selected month. */
  commercialDoneHoursSelectedMonth: z.number(),
  /** Pro bono done hours in the selected month. */
  proBonoDoneHours: z.number(),
  /** proBonoDoneHours × PRO_BONO_HOUR_COST (750 ₽) for the selected month. */
  proBonoCost: z.number(),
  /** Workspace plan capacity = Σ active projects' timeHours. */
  totalPlanHours: z.number(),
  /** Σ task.timeHours allocated to the selected month (any status). */
  totalAllocatedHours: z.number(),
  /** Σ DONE task.timeHours billed in the selected month (completedAt month). */
  totalDoneHours: z.number(),
  /** Per-employee workload rows, sorted by assignedHours desc. */
  employees: z.array(employeeWorkloadSchema),
});
export type ManagementAnalyticsResponseDto = z.infer<typeof managementAnalyticsResponseSchema>;

// ---------- Trend response ----------

export const managementTrendPointSchema = z.object({
  monthKey: z.string(),
  monthLabel: z.string(),
  deliveredHours: z.number(),
  deliveredHoursCommercial: z.number(),
  deliveredHoursProBono: z.number(),
  allocatedHours: z.number(),
  revenue: z.number(),
  totalCost: z.number(),
  margin: z.number(),
  utilizationPct: z.number(),
  breakEvenCoveragePct: z.number(),
  headcount: z.number(),
  capacityHours: z.number(),
  source: z.enum(['closed', 'backfilled', 'recomputed', 'live']),
});
export type ManagementTrendPointDto = z.infer<typeof managementTrendPointSchema>;

export const managementTrendResponseSchema = z.object({
  points: z.array(managementTrendPointSchema),
});
export type ManagementTrendResponseDto = z.infer<typeof managementTrendResponseSchema>;
